'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  deliverables: string[];
  highlight?: boolean;
}

interface ServiceDetail {
  id: string;
  tag: string;
  title: string;
  subtitle: string;
  scope: string;
  scopeItems: string[];
  deliverables: string[];
  pricingTiers: PricingTier[];
  icon: React.ReactNode;
}

const services: ServiceDetail[] = [
  {
    id: 'bank-reconciliation',
    tag: '01',
    title: 'Bank Reconciliation',
    subtitle: 'Monthly accuracy you can rely on',
    scope:
      'Every month, I systematically match your bank and credit card statements against your internal records — catching discrepancies before they become problems. This service is designed for businesses that need clean, verified financials without the overhead of a full-time bookkeeper.',
    scopeItems: [
      'All business bank accounts (checking, savings)',
      'Business credit card statements',
      'Payment processor accounts (Stripe, PayPal, Square)',
      'Petty cash reconciliation',
      'Prior period adjustments if needed',
    ],
    deliverables: [
      'Completed reconciliation report (PDF)',
      'Discrepancy log with resolution notes',
      'Outstanding items schedule',
      'Month-end balance confirmation',
      'Secure delivery via client portal',
    ],
    pricingTiers: [
      {
        name: 'Starter',
        price: '$149',
        period: '/month',
        description: 'Up to 1 bank account, up to 100 transactions',
        deliverables: [
          '1 bank account reconciled',
          'Up to 100 transactions',
          'Monthly reconciliation report',
          'Email support',
        ],
      },
      {
        name: 'Standard',
        price: '$299',
        period: '/month',
        description: 'Up to 3 accounts, up to 300 transactions',
        highlight: true,
        deliverables: [
          'Up to 3 accounts reconciled',
          'Up to 300 transactions',
          'Discrepancy resolution included',
          'Monthly report + summary call',
          'Priority email support',
        ],
      },
      {
        name: 'Growth',
        price: '$499',
        period: '/month',
        description: 'Unlimited accounts, up to 600 transactions',
        deliverables: [
          'Unlimited accounts',
          'Up to 600 transactions',
          'Full discrepancy resolution',
          'Detailed reconciliation report',
          'Dedicated support line',
        ],
      },
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 20l5 5 11-11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'financial-statements',
    tag: '02',
    title: 'Financial Statements',
    subtitle: 'Professionally prepared, CMA-standard reporting',
    scope:
      'I prepare GAAP-aligned financial statements that give you and your stakeholders a clear, accurate picture of your business. Whether you need them for a lender, investor, or internal decision-making, each statement is prepared with the rigor of a CMA-trained professional.',
    scopeItems: [
      'Income statement (Profit & Loss)',
      'Balance sheet as of period end',
      'Statement of cash flows',
      'Notes and disclosures',
      'Comparative period analysis (prior month/year)',
    ],
    deliverables: [
      'Full financial statement package (PDF)',
      'Excel working file (on request)',
      'Variance analysis vs. prior period',
      'Executive summary (1-page)',
      'Secure delivery via client portal',
    ],
    pricingTiers: [
      {
        name: 'Monthly',
        price: '$249',
        period: '/month',
        description: 'Monthly statements for ongoing reporting',
        deliverables: [
          'Income statement',
          'Balance sheet',
          'Cash flow statement',
          'Monthly delivery',
          'Email support',
        ],
      },
      {
        name: 'Quarterly',
        price: '$399',
        period: '/quarter',
        description: 'Full package with comparative analysis',
        highlight: true,
        deliverables: [
          'All three core statements',
          'Comparative period analysis',
          'Notes & disclosures',
          'Executive summary',
          'Review call included',
        ],
      },
      {
        name: 'Annual',
        price: '$899',
        period: '/year',
        description: 'Year-end package with full disclosure',
        deliverables: [
          'Complete annual statements',
          'Full notes & disclosures',
          'Year-over-year comparison',
          'Lender/investor-ready format',
          'Priority turnaround',
        ],
      },
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <rect x="6" y="4" width="28" height="34" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 14h16M12 20h16M12 26h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M26 24l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'bookkeeping',
    tag: '03',
    title: 'Bookkeeping',
    subtitle: 'Clean books, every month, without the overhead',
    scope:
      'Full-cycle bookkeeping that keeps your general ledger accurate, your expenses categorized, and your records audit-ready. I handle the day-to-day recording so you can focus on running your business — with the confidence that your numbers are right.',
    scopeItems: [
      'Daily/weekly transaction recording',
      'Chart of accounts setup and maintenance',
      'Accounts payable and receivable tracking',
      'Payroll entry recording',
      'Month-end closing entries',
    ],
    deliverables: [
      'Updated general ledger (monthly)',
      'Categorized transaction register',
      'A/R and A/P aging reports',
      'Month-end close summary',
      'Secure delivery via client portal',
    ],
    pricingTiers: [
      {
        name: 'Micro',
        price: '$199',
        period: '/month',
        description: 'Sole proprietors, up to 75 transactions',
        deliverables: [
          'Up to 75 transactions/month',
          'Transaction categorization',
          'Monthly ledger update',
          'Email support',
        ],
      },
      {
        name: 'Small Biz',
        price: '$449',
        period: '/month',
        description: 'Small teams, up to 250 transactions',
        highlight: true,
        deliverables: [
          'Up to 250 transactions/month',
          'A/R & A/P tracking',
          'Payroll entry recording',
          'Month-end close + summary',
          'Priority support',
        ],
      },
      {
        name: 'Growing',
        price: '$799',
        period: '/month',
        description: 'Scaling businesses, up to 500 transactions',
        deliverables: [
          'Up to 500 transactions/month',
          'Full A/R & A/P management',
          'Payroll + journal entries',
          'Aging reports included',
          'Dedicated support line',
        ],
      },
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <rect x="5" y="5" width="30" height="35" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <path d="M11 15h18M11 21h18M11 27h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M11 10h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function ServicesPage() {
  const [activeService, setActiveService] = useState<string>(services[0].id);

  const current = services.find((s) => s.id === activeService) ?? services[0];

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Page Header */}
      <section className="pt-28 sm:pt-36 pb-12 sm:pb-16 bg-brand-black">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
          <p
            className="text-primary text-xs font-semibold tracking-widest uppercase mb-4"
            style={{ letterSpacing: '3px', fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
          >
            Services
          </p>
          <h1
            className="text-white mb-4 sm:mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(32px, 6vw, 60px)',
              fontWeight: 700,
              letterSpacing: '-1.5px',
              lineHeight: 1.1,
            }}
          >
            Three services.
            <br />
            One standard.
          </h1>
          <p
            className="text-white/60 text-base sm:text-lg max-w-xl"
            style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
          >
            Scope, deliverables, and transparent pricing for every service I offer — no surprises, no retainer lock-ins.
          </p>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="bg-brand-black border-b border-white/10 sticky top-14 sm:top-16 lg:top-20 z-40">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex overflow-x-auto scrollbar-hide">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveService(s.id)}
                className={`flex-shrink-0 px-5 sm:px-8 py-4 sm:py-5 text-sm font-semibold border-b-2 transition-all duration-300 touch-manipulation whitespace-nowrap ${
                  activeService === s.id
                    ? 'border-primary text-primary' :'border-transparent text-white/50 hover:text-white/80'
                }`}
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px' }}
              >
                <span className="text-xs mr-2 opacity-60">{s.tag}</span>
                {s.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Service Detail */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">

          {/* Service Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-16 sm:mb-20">
            <div className="flex-1">
              <div className="text-primary mb-6">{current.icon}</div>
              <p
                className="text-primary text-xs font-semibold tracking-widest uppercase mb-3"
                style={{ letterSpacing: '3px', fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
              >
                {current.tag} — {current.tag === '01' ? 'Bank Reconciliation' : current.tag === '02' ? 'Financial Statements' : 'Bookkeeping'}
              </p>
              <h2
                className="mb-4"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(28px, 5vw, 48px)',
                  fontWeight: 700,
                  letterSpacing: '-1px',
                  color: '#111111',
                  lineHeight: 1.15,
                }}
              >
                {current.title}
              </h2>
              <p
                className="text-brand-grey700 text-base sm:text-lg"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
              >
                {current.subtitle}
              </p>
            </div>
            <div className="lg:max-w-sm">
              <Link
                href="/#portal"
                className="btn-primary inline-block text-sm px-6 py-3 text-center w-full sm:w-auto"
              >
                Start a Project →
              </Link>
            </div>
          </div>

          {/* Scope + Deliverables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-16 sm:mb-24">
            {/* Scope */}
            <div className="bg-brand-grey50 p-8 sm:p-10 lg:p-12">
              <div className="w-10 h-0.5 bg-primary mb-6" />
              <h3
                className="mb-4 sm:mb-6"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(20px, 3vw, 28px)',
                  fontWeight: 600,
                  color: '#111111',
                  letterSpacing: '-0.5px',
                }}
              >
                Scope of Work
              </h3>
              <p
                className="text-brand-grey700 text-sm sm:text-base mb-6 sm:mb-8"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.75' }}
              >
                {current.scope}
              </p>
              <ul className="space-y-3">
                {current.scopeItems.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-brand-grey700 text-sm"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Deliverables */}
            <div className="bg-brand-black p-8 sm:p-10 lg:p-12">
              <div className="w-10 h-0.5 bg-primary mb-6" />
              <h3
                className="text-white mb-4 sm:mb-6"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(20px, 3vw, 28px)',
                  fontWeight: 600,
                  letterSpacing: '-0.5px',
                }}
              >
                What You Receive
              </h3>
              <p
                className="text-white/60 text-sm sm:text-base mb-6 sm:mb-8"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.75' }}
              >
                Every engagement includes the following deliverables, delivered securely through the client portal.
              </p>
              <ul className="space-y-4">
                {current.deliverables.map((item, i) => (
                  <li
                    key={item}
                    className="flex items-start gap-4 text-white/80 text-sm"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full border border-primary/40 flex items-center justify-center text-primary text-xs font-bold"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                    >
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pricing Tiers */}
          <div>
            <div className="mb-10 sm:mb-12">
              <div className="w-10 h-0.5 bg-primary mb-6" />
              <h3
                className="mb-3"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(24px, 4vw, 36px)',
                  fontWeight: 700,
                  color: '#111111',
                  letterSpacing: '-0.75px',
                }}
              >
                Pricing Tiers
              </h3>
              <p
                className="text-brand-grey700 text-sm sm:text-base max-w-lg"
                style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
              >
                All prices are estimates. Final pricing is confirmed after a brief intake review. No hidden fees.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {current.pricingTiers.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative p-6 sm:p-8 transition-all duration-500 ${
                    tier.highlight
                      ? 'bg-brand-black text-white' :'bg-brand-grey50 text-brand-dark border border-brand-grey100'
                  }`}
                  style={{ transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }}
                >
                  {tier.highlight && (
                    <div
                      className="absolute top-0 left-0 right-0 h-0.5 bg-primary"
                    />
                  )}

                  <p
                    className={`text-xs font-semibold tracking-widest uppercase mb-4 ${tier.highlight ? 'text-primary' : 'text-brand-grey700'}`}
                    style={{ letterSpacing: '2px', fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    {tier.highlight ? '★ Most Popular' : tier.name}
                  </p>

                  <div className="mb-2">
                    <span
                      className={`font-bold ${tier.highlight ? 'text-white' : 'text-brand-dark'}`}
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: 'clamp(32px, 5vw, 44px)',
                        letterSpacing: '-1px',
                      }}
                    >
                      {tier.price}
                    </span>
                    <span
                      className={`text-sm ml-1 ${tier.highlight ? 'text-white/50' : 'text-brand-grey700'}`}
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                    >
                      {tier.period}
                    </span>
                  </div>

                  <p
                    className={`text-sm mb-6 ${tier.highlight ? 'text-white/60' : 'text-brand-grey700'}`}
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.6' }}
                  >
                    {tier.description}
                  </p>

                  <ul className="space-y-2.5 mb-8">
                    {tier.deliverables.map((d) => (
                      <li
                        key={d}
                        className={`flex items-start gap-3 text-sm ${tier.highlight ? 'text-white/75' : 'text-brand-grey700'}`}
                        style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>

                  <Link
                    href="/#portal"
                    className={`block text-center text-sm font-bold py-3 px-6 transition-all duration-300 touch-manipulation ${
                      tier.highlight
                        ? 'bg-primary text-white hover:bg-primary-dark' :'border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white'
                    }`}
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Get Started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="bg-primary py-12 sm:py-16">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3
              className="text-white mb-2"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(20px, 3vw, 28px)',
                fontWeight: 700,
                letterSpacing: '-0.5px',
              }}
            >
              Not sure which tier fits?
            </h3>
            <p
              className="text-white/80 text-sm sm:text-base"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
            >
              Use the estimate calculator or submit an intake form — I'll confirm the right scope for your business.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              href="/#estimate"
              className="inline-block bg-white text-primary font-bold text-sm px-6 py-3 text-center transition-all duration-300 hover:bg-brand-grey50 touch-manipulation"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              Get Estimate
            </Link>
            <Link
              href="/#portal"
              className="inline-block border border-white text-white font-bold text-sm px-6 py-3 text-center transition-all duration-300 hover:bg-white/10 touch-manipulation"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              Client Portal
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
